import {
  faFile,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFileVideo,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { format } from 'date-fns';

import type { Asset, FilterOption } from './types';

export type AssetType = 'image' | 'video' | 'document';

// returns Formatted string (e.g., "1.5 MB", "256 KB")
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const sizeIndex = Math.min(i, sizes.length - 1);

  return `${(bytes / k ** sizeIndex).toFixed(1)} ${sizes[sizeIndex]}`;
}

export function getAssetDisplayName(asset: Asset): string {
  return asset.filename || asset.metadata?.originalFilename || 'Untitled';
}

export function matchesAssetSearch(asset: Asset, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return [asset.filename || asset.metadata?.originalFilename, asset.altText].some((value) =>
    value?.toLowerCase().includes(normalizedQuery),
  );
}

export function getAssetType(contentType: string | undefined): AssetType {
  if (!contentType) return 'document';

  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';

  return 'document';
}

export function isImageAsset(asset: Asset): boolean {
  return getAssetType(asset.contentType) === 'image';
}

export function isVideoAsset(asset: Asset): boolean {
  return getAssetType(asset.contentType) === 'video';
}

export function getFilterCategory(asset: Asset): FilterOption {
  const ct = asset.contentType;
  if (!ct) return 'docs';
  // SVG is an image format; include under Images. Icons (SVGs) filter still matches via contentType in useAssetFilters.
  if (ct.startsWith('image/')) return 'images';
  if (ct.startsWith('video/')) return 'videos';
  if (ct.startsWith('audio/')) return 'audio';
  return 'docs';
}

export function isCroppableAsset(asset: Asset, croppableFormats: string[]): boolean {
  return isImageAsset(asset) && croppableFormats.includes(asset.contentType || '');
}

// returns Formatted date (e.g., "December 19, 2024")
export function formatDate(dateString: string): string {
  return format(new Date(dateString), 'MMMM d, yyyy');
}

// returns FontAwesome icon definition
export function getFileIcon(contentType: string | undefined): IconDefinition {
  const assetType = getAssetType(contentType);

  switch (assetType) {
    case 'image':
      return faFileImage;
    case 'video':
      return faFileVideo;
    case 'document':
      return getDocumentIcon(contentType);
  }
}

function getDocumentIcon(contentType: string | undefined): IconDefinition {
  if (!contentType) return faFile;

  switch (contentType) {
    case 'application/pdf':
      return faFilePdf;
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return faFileLines;
    default:
      return faFile;
  }
}

// Calculates MD5 checksum of a file and returns it in base64 format.
// Required for S3 direct uploads via Active Storage (Content-MD5 header).
export async function calculateFileChecksum(file: File): Promise<string> {
  const SparkMD5 = (await import('spark-md5')).default;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;

      const spark = new SparkMD5.ArrayBuffer();
      spark.append(arrayBuffer);

      const rawMd5 = spark.end(true);
      const base64 = btoa(rawMd5);

      resolve(base64);
    };

    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function decodeImage(file: File): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject();
    };
    img.src = url;
  });
}

export async function validateFileIntegrity(file: File): Promise<string | null> {
  try {
    await file.slice(0, 1024).arrayBuffer();
  } catch {
    return 'This file appears corrupted and cannot be read.';
  }

  if (file.type.startsWith('image/')) {
    try {
      await decodeImage(file);
    } catch {
      return 'The uploaded file appears to be corrupted. Try uploading a different file.';
    }
  }

  return null;
}

export function isValidUrl(url: string): boolean {
  if (!url?.trim()) return false;

  try {
    const { protocol, hostname } = new URL(url);

    if (protocol !== 'http:' && protocol !== 'https:') return false;
    if (!hostname || hostname === 'localhost') return false;

    return true;
  } catch {
    return false;
  }
}
